<template>
  <div class="select-location">
    <div class="dropdown-container" ref="dropdownRef">
      <v-text-field
        v-model="inputValue"
        :placeholder="placeholder"
        outlined
        clearable
        @focus="handleInputFocus"
        @input="handleInputChange"
        class="text-field"
        hide-details="auto"
      >
        <template #append>
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            size="20"
            color="primary"
          ></v-progress-circular>
        </template>
      </v-text-field>
      <div v-if="showDropdown && validPredictions.length > 0" class="dropdown">
        <button
          v-for="prediction in validPredictions"
          :key="prediction.place_id"
          class="dropdown-item"
          type="button"
          @click="handleSelectPlace(prediction)"
        >
          <span class="dropdown-item-main">
            {{ prediction.structured_formatting.main_text }}
          </span>
          <span class="dropdown-item-secondary">
            {{ prediction.structured_formatting.secondary_text }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { Loader } from '@googlemaps/js-api-loader'
  import { useDebounce } from '@/composables/useDebounce'

  export default {
    props: {
      value: {
        type: Object,
        default: () => null,
      },
      placeholder: {
        type: String,
        default: 'Enter a location',
      },
    },
    emits: ['update:value'],
    data() {
      return {
        inputValue: this.value?.fullAddress || this.value?.name || '',
        predictions: [],
        validPredictions: [],
        showDropdown: false,
        isLoading: false,
        dropdownRef: null,
        autocompleteService: null,
        placesService: null,
      }
    },
    computed: {
      debouncedInput() {
        return useDebounce(this.inputValue, 300)
      },
    },
    watch: {
      debouncedInput: {
        immediate: true,
        handler() {
          this.fetchPredictions()
        },
      },
      value: {
        immediate: true,
        handler(newValue) {
          if (!newValue?.lat && !newValue?.lng) {
            this.inputValue = ''
          }
        },
      },
    },
    mounted() {
      this.initializeServices()
    },
    methods: {
      async initializeServices() {
        const loader = new Loader({
          apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
          libraries: ['places'],
        })

        try {
          const google = await loader.load()
          this.autocompleteService = new google.maps.places.AutocompleteService()
          const div = document.createElement('div')
          div.style.display = 'none'
          document.body.appendChild(div)
          this.placesService = new google.maps.places.PlacesService(div)
        } catch (error) {
          console.log('Error initializing Google Maps services:', error)
        }
      },
      async fetchPredictions() {
        if (!this.debouncedInput.value || !this.autocompleteService) {
          this.predictions = []
          this.validPredictions = []
          return
        }

        try {
          this.isLoading = true
          this.autocompleteService.getPlacePredictions(
            { input: this.debouncedInput.value },
            (results, status) => {
              if (status === 'OK') {
                this.predictions = results
                this.validPredictions = results.filter(
                  (prediction) => prediction.structured_formatting
                )
                if (this.validPredictions.length > 0 && !(this.value.lat && this.value.lng)) {
                  this.showDropdown = true
                } else {
                  this.showDropdown = false
                }
              } else {
                this.predictions = []
                this.validPredictions = []
              }
            }
          )
        } catch (error) {
          console.log('Error fetching predictions:', error)
          this.predictions = []
          this.validPredictions = []
        } finally {
          this.isLoading = false
        }
      },
      handleInputChange(value) {
        this.inputValue = value
        if (!value) {
          this.$emit('update:value', null)
        }
      },
      handleInputFocus() {
        if (this.inputValue) {
          this.showDropdown = true
        }
      },
      handleSelectPlace(prediction) {
        if (!this.placesService) {
          return
        }

        this.placesService.getDetails(
          {
            placeId: prediction.place_id,
            fields: ['geometry', 'formatted_address', 'name'],
          },
          (place, status) => {
            if (status === 'OK' && place?.geometry?.location) {
              const location = {
                name: place.name || prediction.structured_formatting.main_text,
                lat: parseFloat(place.geometry.location.lat().toFixed(3)),
                lng: parseFloat(place.geometry.location.lng().toFixed(3)),
                fullAddress: prediction.description,
              }
              this.inputValue = prediction.description || place.name || ''
              this.$emit('update:value', location)
              this.showDropdown = false
              this.predictions = []
              this.validPredictions = []
            }
          }
        )
      },
    },
  }
</script>

<style scoped>
  .select-location {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    margin-bottom: 2rem;
  }

  .dropdown-container {
    position: relative;
  }

  .text-field {
    width: 100%;
  }

  .dropdown {
    position: absolute;
    z-index: 10;
    width: 100%;
    background-color: #1c1c1e;
    border: 1px solid #2c2c2e;
    border-radius: 0 0 0.5rem 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-height: 320px;
    overflow-y: auto;
    margin-top: 0;
  }

  .dropdown-item {
    width: 100%;
    padding: 0.75rem 1rem;
    text-align: left;
    background-color: transparent;
    color: white;
    border-bottom: 1px solid #2c2c2e;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .dropdown-item:hover {
    background-color: #18273c;
  }

  .dropdown-item:last-child {
    border-bottom: none;
  }

  .dropdown-item-main {
    display: block;
    font-size: 0.9375rem;
  }

  .dropdown-item-secondary {
    font-size: 0.8125rem;
    color: #9ca3af;
  }
</style>
